// assetsDisplay — the register's shared display vocabulary (the register build's
// extraction of the story-proven cells; closes the CATEGORY_ICON ×3 dedup
// rider). One home for: the category glyph map, the status glyph/badge, the
// identity cell, the location cell (kind icon carries the kind — icon-only,
// the P ruling), the observations cell speaking the registry's labels, and the
// copiable reference (batch-1: any reference shown in list/peek/group header
// is copiable).

import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { AssetHiddenRef, AssetListItem, AssetLocationRef } from '@ems-app/schemas/assets/responses';
import {
  CATEGORY_DISPLAY_ORDER,
  CATEGORY_SECTIONS,
  categoriesFollowing,
  isCountIncomplete,
  ratingUnitFor,
  type AssetCategory,
} from '@ems-app/schemas/assets/categories';
import { OBSERVATION_FAMILIES, type ObservationFamily } from '@ems-app/schemas/observations/registry';
import { icons, type Icon as IconType } from '../ui/icons.js';
import { Badge, type BadgeVariant } from '../ui/Badge.js';
import { InlineCopy } from '../ui/CopyField.js';
import { EntityRef } from './EntityRef.js';
import {
  IMPACT_CARD,
  ObservationCard,
  ObservationMark,
  ObservationMarkRow,
  kindMarkSummaries,
} from './observations.js';

export type AssetStatus = AssetListItem['status'];

// The category SECTIONS + display order live in the CHARTER (ADR-067 —
// one declared home; the picker grids, the products chips and the settings
// matrix all read the same cut). Re-exported here so display consumers keep
// one import site.
export { CATEGORY_SECTIONS, CATEGORY_DISPLAY_ORDER };

export const CATEGORY_ICON: Record<AssetCategory, IconType> = {
  cabinet: icons.cabinet,
  power_system: icons.powerSystem,
  rectifier_module: icons.module,
  inverter_module: icons.inverter,
  solar_charger: icons.sourceSolar,
  power_system_controller: icons.controller,
  generator_controller: icons.controller,
  cooling_controller: icons.controller,
  fuel_sensor: icons.fuelLevel,
  gateway: icons.gateway,
  battery_string: icons.batteryString,
  battery_module: icons.batteryModule,
  solar_string: icons.solarString,
  solar_panel: icons.solarPanel,
  ups: icons.ups,
  generator: icons.sourceGenerator,
  fuel_tank: icons.fuelTank,
  ac_unit: icons.sourceCooling,
  wind_turbine: icons.sourceWind,
};

export const STATUS_GLYPH: Record<AssetStatus, { icon: IconType; tone: string }> = {
  deployed: { icon: icons.statusSuccess, tone: 'text-[var(--color-success)]' },
  stocked: { icon: icons.statusInfo, tone: 'text-[var(--color-info)]' },
  on_hold: { icon: icons.statusPaused, tone: 'text-[var(--color-warning)]' },
  retired: { icon: icons.statusArchived, tone: 'text-[var(--color-text-secondary)]' },
  lost: { icon: icons.statusError, tone: 'text-[var(--color-danger)]' },
};

export const STATUS_BADGE: Record<AssetStatus, BadgeVariant> = {
  deployed: 'success',
  stocked: 'info',
  on_hold: 'warning',
  retired: 'neutral',
  lost: 'danger',
};

export const LOCATION_KIND_ICON: Record<AssetLocationRef['kind'], IconType> = {
  site: icons.site,
  depot: icons.warehouse,
  site_storage: icons.siteStorage,
  vehicle_stock: icons.vehicle,
};

/** A place or a unit the viewer may not see — the server sends its KIND alone (ADR-066 §9). */
export function isHiddenRef(ref: object): ref is AssetHiddenRef {
  return 'hidden' in ref && (ref as AssetHiddenRef).hidden === true;
}

/** The words for a related place or unit: its name, or — one the viewer may not see — its kind ("a depot"). */
export function refWords(ref: { label: string } | AssetHiddenRef, t: TFunction): string {
  return isHiddenRef(ref) ? t(`hidden.${ref.kind}`) : ref.label;
}

/** The product line — "Manufacturer Name" (the model group header + titles). */
export function productLineOf(item: Pick<AssetListItem, 'model'>): string | null {
  if (!item.model) return null;
  return item.model.manufacturer ? `${item.model.manufacturer} ${item.model.label}` : item.model.label;
}

export function AssetStatusBadge({ status, t }: { status: AssetStatus; t: TFunction }) {
  return <Badge variant={STATUS_BADGE[status]}>{t(`status.${status}`)}</Badge>;
}

export function AssetStatusGlyph({ status, t }: { status: AssetStatus; t: TFunction }) {
  const g = STATUS_GLYPH[status];
  const word = t(`status.${status}`);
  return (
    <span className="inline-flex items-center" title={word}>
      <g.icon size={18} weight="fill" aria-hidden className={g.tone} />
      <span className="sr-only">{word}</span>
    </span>
  );
}

/** The product word + the WEIGHT its catalog tier earns (story R-IV.6):
 *  canonical catalog / rated generic = primary; LOCAL model = muted (complete,
 *  unverified); no model = disabled ("Generic X" when a rating answers, the
 *  no-model nudge otherwise). One derivation, two renders (plain line · the
 *  identity link) so the tier can never read differently between them. */
function productFace(item: AssetListItem, t: TFunction): { text: string; tone: string; title?: string; ltr: boolean } {
  const count = item.unit_count != null ? ` ×${item.unit_count}` : '';
  if (item.model) {
    const unverified = !item.model.is_canonical && !item.model.is_generic;
    return {
      text: `${item.model.manufacturer ? `${item.model.manufacturer} ` : ''}${item.model.label}${count}`,
      tone: unverified ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-primary)]',
      title: unverified ? t('cell.local') : undefined,
      ltr: true,
    };
  }
  const rated = item.rating != null;
  return {
    text: `${rated ? t('cell.generic', { category: t(`category.${item.category}`) }) : t('list.noModel')}${count}`,
    tone: rated ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-disabled)]',
    ltr: false,
  };
}

export function AssetProductLine({ item, t }: { item: AssetListItem; t: TFunction }) {
  const f = productFace(item, t);
  return (
    <div className={`truncate text-sm font-medium ${f.tone}`} dir={f.ltr ? 'ltr' : undefined} title={f.title}>
      {f.text}
    </div>
  );
}

/** The register row's identity. Pass `to` on a list whose entity HAS a page:
 *  the name becomes the row's own quiet `EntityRef` — it OPENS the page while a
 *  bare-row click peeks (FRONTEND-PATTERNS §Row interaction). Without it the
 *  name is plain text (a surface with no page to open). */
export function AssetIdentityCell({ item, t, to }: { item: AssetListItem; t: TFunction; to?: string }) {
  // No embedded status glyph (T batch 4 — it read as a mystery mark): on the
  // phone the STATUS COLUMN keeps its own glyph face, so identity stays pure.
  const f = productFace(item, t);
  return (
    <div className="min-w-0">
      {to ? (
        // `quiet` + no onPeek = the row's own identity: plain at rest, link on
        // hover, ⌘-click opens a tab. The tier weight rides className so the
        // link and the plain line can never diverge.
        <span dir={f.ltr ? 'ltr' : undefined} className="flex min-w-0" title={f.title}>
          <EntityRef quiet name={f.text} to={to} className={`truncate text-sm ${f.tone}`} />
        </span>
      ) : (
        <AssetProductLine item={item} t={t} />
      )}
      <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
        <span className="truncate">{t(`category.${item.category}`)}</span>
        <span aria-hidden className="text-[var(--color-text-disabled)]">·</span>
        <InlineCopy value={item.reference} />
      </div>
    </div>
  );
}

/** The book-status PHONE face (T batch 5 — the books phone row reads like the
 *  register's: identity + icon columns): the receipt glyph in the status's
 *  tone. A distinct silhouette from the OPS status circles, so the two glyph
 *  columns sitting side by side never blur. */
export function AssetBookStatusGlyph({ status, t }: { status: 'on_books' | 'written_off' | 'disposed'; t: TFunction }) {
  const tone = {
    on_books: 'text-[var(--color-success)]',
    written_off: 'text-[var(--color-warning)]',
    disposed: 'text-[var(--color-text-disabled)]',
  }[status];
  const word = t(`bookStatus.${status}`);
  return (
    <span className={`inline-flex ${tone}`} title={word}>
      <icons.invoicing size={18} aria-hidden />
      <span className="sr-only">{word}</span>
    </span>
  );
}

/** The location column's PHONE face (T batch 4): the kind icon ALONE — the
 *  place name + ref live one tap away in the peek; the freed width keeps the
 *  status + observation icon columns on a 375 screen. */
export function AssetLocationMark({ item, t }: { item: AssetListItem; t: TFunction }) {
  const loc = item.location;
  if (!loc) return emDash;
  const KindIcon = LOCATION_KIND_ICON[loc.kind];
  const label = `${t(`cell.kind.${loc.kind}`)} · ${loc.label}`;
  return (
    <span className="inline-flex" title={label}>
      <KindIcon size={16} aria-hidden className="text-[var(--color-text-secondary)]" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** LOCATION carries identity (story R-IV.6, re-cut at T): the kind reads as an
 *  ICON (T ruling — supersedes the batch-2 no-icons-in-list-rows law; tooltip +
 *  sr-only carry the word), the place name beside it (the reserved-for earmark
 *  rides there — reservation lives HERE, not under Status; last-known died at
 *  T: the Lost status + date already say it), and line 2 carries the QUIET
 *  facts: the Unallocated marker (batch 3 — info-toned, never alarming: pool
 *  stock is a fact, not a defect) before the place's COPIABLE reference, each
 *  only when present — so plain storage rows read one line and sites two. THE
 *  shared asset-location cell: register · books · every future list needing an
 *  asset's place. */
export function AssetLocationCell({ item, t }: { item: AssetListItem; t: TFunction }) {
  const loc = item.location;
  if (!loc) return <span className="text-[var(--color-text-disabled)]">{t('list.noLocation')}</span>;
  const KindIcon = LOCATION_KIND_ICON[loc.kind];
  const kindWord = t(`cell.kind.${loc.kind}`);
  const reserved = item.status === 'stocked' && item.reserved_for ? t('cell.reserved', { site: refWords(item.reserved_for, t) }) : null;
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="inline-flex shrink-0" title={kindWord}>
          <KindIcon size={14} aria-hidden className="text-[var(--color-text-secondary)]" />
          <span className="sr-only">{kindWord}</span>
        </span>
        <span className="truncate">{loc.label}</span>
        {reserved && <span className="shrink-0 truncate text-xs text-[var(--color-text-secondary)]">· {reserved}</span>}
      </div>
      {(item.is_unallocated || loc.reference) && (
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 ps-5 text-xs text-[var(--color-text-secondary)]">
          {item.is_unallocated && (
            <span className="shrink-0 font-medium text-[var(--color-info)]">{t('list.unallocated')}</span>
          )}
          {item.is_unallocated && loc.reference && <span aria-hidden className="text-[var(--color-text-disabled)]">·</span>}
          {loc.reference && <InlineCopy value={loc.reference} />}
        </div>
      )}
    </div>
  );
}

/** The observations cell — the registry's labels, worst-impact first; the bare
 *  MARK face for the degraded column. */
const IMPACT_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

/** A family's display label in the active language (registry-carried trilingual). */
export function familyLabel(f: ObservationFamily, lang: string): string {
  const label = OBSERVATION_FAMILIES[f]?.label;
  if (!label) return f;
  return (label as Record<string, string>)[lang.split('-')[0]!] ?? label.en;
}

export function assetObservationFamilies(item: Pick<AssetListItem, 'observations'>): ObservationFamily[] {
  return [...(item.observations as ObservationFamily[])].sort(
    (a, b) => (IMPACT_ORDER[OBSERVATION_FAMILIES[a]?.impact ?? 'low'] ?? 2) - (IMPACT_ORDER[OBSERVATION_FAMILIES[b]?.impact ?? 'low'] ?? 2),
  );
}

/** The row face (ADR-061 §5, T re-cut): counted PER-KIND marks, no words in
 *  the cell — the sentence ("Missing: Owner · Cost …") rides title + sr-only,
 *  and the peek carries the cards. Kills the FR text-cropping and gives every
 *  register the same narrow, same-geometry column. */
export function AssetObservationsCell({ item, lang, t }: { item: AssetListItem; lang: string; t: TFunction }) {
  const fams = assetObservationFamilies(item);
  if (fams.length === 0) return emDash;
  const found = fams.map((f) => ({ kind: OBSERVATION_FAMILIES[f]!.kind, impact: OBSERVATION_FAMILIES[f]!.impact }));
  const marks = kindMarkSummaries(found);
  const label = marks
    .map((m) => {
      const words = fams.filter((f) => OBSERVATION_FAMILIES[f]!.kind === m.kind).map((f) => familyLabel(f, lang));
      return `${t(`observations.kindWord.${m.kind}`)}: ${words.join(' · ')}`;
    })
    .join(' — ');
  return <ObservationMarkRow marks={marks} label={label} />;
}

export function AssetObservationsMark({ item }: { item: AssetListItem }) {
  const fams = assetObservationFamilies(item);
  const top = fams[0];
  if (!top) return null;
  const def = OBSERVATION_FAMILIES[top]!;
  return <ObservationMark kind={def.kind} impact={def.impact} count={fams.length} bare />;
}

/** em-dash placeholder shared by the asset columns. */
export const emDash: ReactNode = <span className="text-[var(--color-text-disabled)]">—</span>;

// ── the record's OWN state: the work band + the finding cards ───────────────
// Both the peek and the detail page carry them (the peek owns completeness, the
// page is the same record at page width), so they live here rather than in
// either surface — the cell-identity contract: variants arrive as props, never
// as a fork.

/** The band above the record — "N to decide · N to fill in", jumping to the
 *  cards. The peek's ONE tint budget (ADR-061); renders nothing when clean. */
export function AssetWorkBand({
  families,
  onJump,
  t,
}: {
  families: ObservationFamily[];
  onJump: () => void;
  t: TFunction;
}) {
  const decide = families.filter((f) => OBSERVATION_FAMILIES[f]?.kind !== 'missing').length;
  const fill = families.length - decide;
  if (families.length === 0) return null;
  return (
    <button
      type="button"
      onClick={onJump}
      className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] outline-none focus-ring hover:bg-[var(--color-warning)]/15 ${IMPACT_CARD.medium}`}
    >
      <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
        {decide > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <ObservationMark kind="conflict" bare />
            {t('peek.band.decide', { count: decide })}
          </span>
        )}
        {fill > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <ObservationMark kind="missing" bare />
            {t('peek.band.fill', { count: fill })}
          </span>
        )}
      </span>
      <icons.caretDown aria-hidden size={14} className="shrink-0 text-[var(--color-text-secondary)]" />
    </button>
  );
}

/** Every live finding worst-first, or the one quiet complete line. The server
 *  already scoped the set to this viewer (books families reach books:read
 *  holders only) — render what arrives. */
export function AssetObservationCards({
  families,
  lang,
  t,
}: {
  families: ObservationFamily[];
  lang: string;
  t: TFunction;
}) {
  if (families.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
        <icons.check size={14} aria-hidden className="text-[var(--color-success)]" />
        {t('peek.complete')}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {families.map((f) => {
        const def = OBSERVATION_FAMILIES[f]!;
        return (
          <ObservationCard
            key={f}
            kind={def.kind}
            impact={def.impact}
            kindLabel={t(`observations.kindWord.${def.kind}`)}
            subject={familyLabel(f, lang)}
          />
        );
      })}
    </div>
  );
}

// ── the DRAFT derivation + the invite strip (the add/edit surfaces) ──────────
// The strip mirrors the SERVER's v1 read-time derivation (assets service
// FAMILY_PREDICATE — ADR-064 "read-time columns"): what it invites on a live
// draft is exactly what the saved row's Observations cell will show. Books
// families derive only when the caller opens that door (books category + the
// books gate — a non-holder can't fill them, so inviting them would lie).

const MODEL_F = categoriesFollowing('model');
const SERIAL_F = categoriesFollowing('serial');
const REACH_F = categoriesFollowing('reach');
const PARENT_F = categoriesFollowing('parent');
const CONDITION_F = categoriesFollowing('condition');
const SOH_F = categoriesFollowing('soh');

export interface AssetDraftFacts {
  category: AssetCategory;
  /** The register half — null = out of this draft's scope (the books drawer
   *  edits money facts only, so register families would be noise there). */
  register: {
    /** A product answers the what-question: a non-generic model chosen, or a
     *  local birth with a typed name. */
    modelSelected: boolean;
    /** The generic row is the pick — complete only WITH a rating (ADR-065). */
    generic: boolean;
    ratingValue: number | null;
    unitCount: number | null;
    /** Whether the row already tracks member children (a fully-tracked group
     *  legitimately counts zero — the units LOCK's refinement). Creation
     *  drafts have none; the Edit drawer passes the record's real state. */
    hasTrackedMembers?: boolean;
    serial: string;
    /** REACH, not IP (the charter law): an own address OR a via edge declared
     *  on the draft satisfies the invite. */
    reachDeclared: boolean;
    /** The resolved capability (model override ?? the charter three-state) +
     *  whether the draft lands DEPLOYED — the reach invite fires only for a
     *  capable unit headed to a site (the charter's deployed-only law). */
    networkCapable: boolean;
    deployed: boolean;
    /** "Part of" — the register flags a deployed unit of a category that is normally held with no holder. */
    parentId?: string | null;
    /** The condition grade and the state of health — the register's two quiet facts, where the charter asks them. */
    condition?: string | null;
    soh?: number | null;
  } | null;
  /** The books half — null = the books door is closed for this draft
   *  (non-books category or no books gate) — no books families derive. */
  books: { ownerId: string | null; cost: number | null; acquiredOn: string | null; /** A cost is expected only where one exists by nature — a purchased unit (or one whose mode is not said yet); the register's law. */ mode?: string | null } | null;
}

export function draftObservationFamilies(d: AssetDraftFacts): ObservationFamily[] {
  const out: ObservationFamily[] = [];
  if (d.register) {
    const r = d.register;
    if (
      MODEL_F.includes(d.category) &&
      ((!r.modelSelected && !r.generic) || (r.generic && r.ratingValue === null && ratingUnitFor(d.category) !== null))
    ) {
      out.push('asset.missing_model');
    }
    if (isCountIncomplete(d.category, r.unitCount, r.hasTrackedMembers ?? false)) out.push('asset.missing_units');
    if (SERIAL_F.includes(d.category) && r.serial.trim() === '') out.push('asset.missing_serial');
    if (REACH_F.includes(d.category) && r.networkCapable && r.deployed && !r.reachDeclared) {
      out.push('asset.missing_reach');
    }
    // The same families the register derives once the row exists (`FAMILY_PREDICATE`) — a host that holds the fact passes it.
    if (r.parentId !== undefined && PARENT_F.includes(d.category) && r.deployed && !r.parentId) out.push('asset.missing_parent');
    if (r.condition !== undefined && CONDITION_F.includes(d.category) && !r.condition) out.push('asset.missing_condition');
    if (r.soh !== undefined && SOH_F.includes(d.category) && r.soh === null) out.push('asset.missing_soh');
  }
  if (d.books) {
    if (d.books.ownerId === null) out.push('asset.books_missing_owner');
    if (d.books.cost === null && (d.books.mode == null || d.books.mode === 'purchased')) out.push('asset.books_missing_cost');
    if (d.books.acquiredOn === null) out.push('asset.books_missing_acquired');
  }
  return out.sort(
    (a, b) =>
      (IMPACT_ORDER[OBSERVATION_FAMILIES[a]?.impact ?? 'low'] ?? 2) - (IMPACT_ORDER[OBSERVATION_FAMILIES[b]?.impact ?? 'low'] ?? 2) ||
      a.localeCompare(b),
  );
}

/** The gentle invite (never a gate — ADR-063): a quiet strip naming what is
 *  still open on the draft, each mark carrying its family's real impact; the
 *  complete state is one green line. Hosts: the wizard's Identity/Review steps
 *  and both edit drawers — separated from whatever sits above by a hairline. */
export function InviteStrip({ families, lang, t }: { families: ObservationFamily[]; lang: string; t: TFunction }) {
  return (
    <div className="mt-2 border-t border-[var(--color-border)] pt-4">
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface-alt)] px-4 py-3 text-sm">
        {families.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 text-[var(--color-text-primary)]">
            <icons.check size={15} aria-hidden className="text-[var(--color-success)]" />
            {t('invite.complete')}
          </span>
        ) : (
          <>
            <p className="text-xs text-[var(--color-text-secondary)]">{t('invite.later')}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {families.map((f) => {
                const def = OBSERVATION_FAMILIES[f]!;
                return (
                  <span key={f} className="inline-flex items-center gap-1.5 text-xs text-[var(--color-text-primary)]">
                    <ObservationMark kind={def.kind} impact={def.impact} bare />
                    <span className="inline-block first-letter:uppercase">{familyLabel(f, lang)}</span>
                  </span>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
